// src/utils/timeBoxHelpers.ts
// TimeBox 時間管理工具函數

/**
 * 計算兩個時間之間的時長（分鐘）
 * @param startTime 開始時間
 * @param endTime 結束時間
 * @returns 時長（分鐘）
 */
export function calculateDuration(startTime: Date, endTime: Date): number {
  const diffMs = endTime.getTime() - startTime.getTime();
  return Math.floor(diffMs / (1000 * 60));
}

/**
 * 基於 duration 計算結束時間
 * @param startTime 開始時間
 * @param durationMinutes 時長（分鐘）
 * @returns 計算後的結束時間
 */
export function calculateEndTimeFromDuration(
  startTime: Date,
  durationMinutes: number
): Date {
  const endTime = new Date(startTime);
  endTime.setMinutes(endTime.getMinutes() + durationMinutes);
  return endTime;
}

/**
 * 驗證時間範圍是否有效
 * @param start 開始時間
 * @param end 結束時間
 * @returns 是否有效
 */
export function validateTimeRange(start: Date, end: Date): boolean {
  return start < end;
}

/**
 * 確保結束日期不早於開始日期
 * @param startDate 開始日期
 * @param endDate 結束日期
 * @returns 調整後的結束日期
 */
export function adjustEndDateIfNeeded(
  startDate: Date,
  endDate: Date
): Date {
  // 只比較日期部分，不考慮時間
  const startDateOnly = new Date(startDate);
  startDateOnly.setHours(0, 0, 0, 0);

  const endDateOnly = new Date(endDate);
  endDateOnly.setHours(0, 0, 0, 0);

  if (endDateOnly < startDateOnly) {
    return new Date(startDate);
  }

  return endDate;
}

/**
 * 組合日期字串和時間字串成 Date 對象
 * @param dateStr 日期字串（YYYY-MM-DD）
 * @param timeStr 時間字串（HH:mm）
 * @returns Date 對象
 */
export function combineDateTime(dateStr: string, timeStr: string): Date {
  // 解析日期字串避免時區問題
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);

  // 使用本地時間構建 Date 對象
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

/**
 * 解構 Date 對象為日期字串和時間字串
 * @param date Date 對象
 * @returns { dateStr: string, timeStr: string }
 */
export function deconstructDateTime(date: Date): {
  dateStr: string;
  timeStr: string;
} {
  // 使用本地時間避免時區轉換問題
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`; // YYYY-MM-DD

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const timeStr = `${hours}:${minutes}`;

  return { dateStr, timeStr };
}

/**
 * 計算新的結束時間（維持原有 duration）
 * 當開始時間變更時使用
 * @param newStartTime 新的開始時間
 * @param originalDuration 原有的時長（分鐘）
 * @returns 新的結束時間
 */
export function calculateNewEndTime(
  newStartTime: Date,
  originalDuration: number
): Date {
  // 如果原有 duration 小於等於 0，預設為 30 分鐘
  const duration = originalDuration > 0 ? originalDuration : 30;
  return calculateEndTimeFromDuration(newStartTime, duration);
}
